export type PyAnalystEndpointSpec = {
  method?: string;
  headers?: Record<string, string>;
  function_url?: string;
  sample_payload?: Record<string, unknown>;
};

export type PyAnalystEndpointConfig = PyAnalystEndpointSpec & {
  key: string;
};

export type PyAnalystEndpointsResponse = {
  status_code?: number;
  message?: string;
  data?: Record<string, PyAnalystEndpointSpec>;
};

export type PyAnalystReportItem = {
  analyst_id?: string;
  s3_key?: string;
};

export type PyAnalystReportResponse = {
  status_code?: number;
  message?: string;
  data?: PyAnalystReportItem[];
};

export const PYANALYST_ENDPOINT_ATTR = 'pyanalyst_endpoint';
export const PYANALYST_ENDPOINT_CONFIG_ATTR = 'pyanalyst_endpoint_config';

export function parsePyAnalystEndpointConfig(raw: unknown): PyAnalystEndpointConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const config = raw as PyAnalystEndpointConfig;
  if (!config.key || !config.function_url) return null;
  return config;
}

export function buildEndpointConfigFromSelection(
  key: string,
  spec: PyAnalystEndpointSpec
): PyAnalystEndpointConfig {
  return {
    key,
    method: spec.method ?? 'POST',
    headers: spec.headers ?? {},
    function_url: spec.function_url ?? '',
    sample_payload: spec.sample_payload ?? {},
  };
}

export type PyAnalystDateFilter = {
  column: string;
  from: string;
  to: string;
};

export function extractDateFiltersFromPayload(samplePayload?: Record<string, unknown>): PyAnalystDateFilter[] {
  const filters = samplePayload?.filters;
  if (!Array.isArray(filters)) return [];

  return filters
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const filter = entry as { column?: string; value?: { from?: string; to?: string } };
      const from = filter.value?.from;
      const to = filter.value?.to;
      if (!filter.column || !from || !to) return null;
      return { column: filter.column, from, to };
    })
    .filter((item): item is PyAnalystDateFilter => item !== null);
}

export function applyDateFiltersToPayload(
  samplePayload: Record<string, unknown>,
  dateFilters: PyAnalystDateFilter[]
): Record<string, unknown> {
  const payload = JSON.parse(JSON.stringify(samplePayload)) as Record<string, unknown>;
  const filters = payload.filters;
  if (!Array.isArray(filters)) return payload;

  dateFilters.forEach(({ column, from, to }) => {
    const match = filters.find(
      (entry) => entry && typeof entry === 'object' && (entry as { column?: string }).column === column
    ) as { column?: string; value?: Record<string, string> } | undefined;
    if (match) {
      match.value = { from, to };
    }
  });

  return payload;
}

export function resolvePyAnalystHeaders(
  headers: Record<string, string> | undefined,
  token: string
): Record<string, string> {
  const resolved: Record<string, string> = {};
  Object.entries(headers ?? {}).forEach(([name, value]) => {
    resolved[name] = String(value).replace(/\$\{Authorization\}/g, `Bearer ${token}`);
  });
  return resolved;
}
